/**
 * SEO identity for every public URL (homepage, Ghiduri, legal).
 * Used both by `<Seo>` in the SPA and by the build-time HTML prerender.
 */

export const SITE_URL = "https://www.aifoiparcurs.ro";

export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;
export const DEFAULT_OG_IMAGE_ALT =
  "AIFoiParcurs — Foi de parcurs online, completate automat";

export type PublicPageKind = "home" | "article" | "news" | "legal" | "hub";

export type PublicPage = {
  path: string;
  title: string;
  description: string;
  /** Visible H1 — used in Article/WebPage JSON-LD. */
  headline: string;
  kind: PublicPageKind;
  /** Site-relative share image. Homepage and legal omit this and keep `/og-image.jpg`. */
  ogImage?: string;
  ogImageAlt?: string;
};

export const publicPages: PublicPage[] = [
  {
    path: "/",
    kind: "home",
    title: "Aplicație foi de parcurs online | AIFoiParcurs",
    description:
      "AIFoiParcurs completează automat foile de parcurs pentru firme și PFA-uri: rute, alimentări, validare consum și export rapid pentru contabil.",
    headline: "Aplicație pentru foi de parcurs online, completate automat.",
  },
  {
    path: "/ghid-foaie-parcurs-anaf",
    kind: "article",
    title: "Ghid foaie de parcurs ANAF 2026: cerințe, model și deducere | AIFoiParcurs",
    description:
      "Ce trebuie să conțină o foaie de parcurs ca să fie acceptată la control ANAF: cele 4 elemente obligatorii, deducere 50% vs 100%, format acceptat și greșeli frecvente.",
    headline: "Foaia de parcurs și cerințele ANAF în 2026",
    ogImage: "/og/ghid-foaie-parcurs-anaf.png",
    ogImageAlt: "Cerințe ANAF 2026 pentru foaia de parcurs",
  },
  {
    path: "/deducere-100-combustibil",
    kind: "article",
    title: "Deducere 100% combustibil: cum o obții și ce verifică ANAF | AIFoiParcurs",
    description:
      "Cum deduci integral TVA-ul și cheltuielile cu combustibilul auto: condițiile legale, exemple de calcul, vehiculele exceptate și rolul foii de parcurs ca document justificativ.",
    headline: "Deducere 100% combustibil: condițiile pentru TVA și impozit",
    ogImage: "/og/deducere-100-combustibil.png",
    ogImageAlt: "Deducere 100% combustibil — comparație 50% vs 100%",
  },
  {
    path: "/foi-parcurs-pfa",
    kind: "article",
    title: "Foi de parcurs pentru PFA: obligații, deducere și model | AIFoiParcurs",
    description:
      "PFA-urile care folosesc un autovehicul în activitate sunt obligate să întocmească foi de parcurs pentru a deduce integral cheltuielile. Ghid practic cu model și exemple.",
    headline: "Foi de parcurs pentru PFA: obligații, deducere și model",
    ogImage: "/og/foi-parcurs-pfa.png",
    ogImageAlt: "Foi de parcurs pentru PFA",
  },
  {
    path: "/model-foaie-parcurs",
    kind: "article",
    title: "Model foaie de parcurs lunară: structură, elemente și exemplu | AIFoiParcurs",
    description:
      "Cum arată o foaie de parcurs lunară conformă cu cerințele ANAF: elementele obligatorii, structura recomandată, exemplu completat și diferența față de FAZ.",
    headline: "Model foaie de parcurs lunară: structură și exemplu completat",
    ogImage: "/og/model-foaie-parcurs.png",
    ogImageAlt: "Model foaie de parcurs lunară",
  },
  {
    path: "/control-anaf-cheltuieli-autoturism",
    kind: "article",
    title:
      "Control ANAF cheltuieli autoturism: documente pe care trebuie să le ai | AIFoiParcurs",
    description:
      "Ce verifică ANAF la cheltuielile cu autoturismele de firmă: foaie de parcurs, bonuri, facturi, ITP, asigurări, contracte și greșeli care atrag recalcularea deducerii.",
    headline:
      "Control ANAF pentru cheltuielile cu autoturismele: ce documente trebuie să ai pregătite",
    ogImage: "/og/control-anaf-cheltuieli-autoturism.png",
    ogImageAlt: "Pregătire control ANAF — documente pentru cheltuieli auto",
  },
  {
    path: "/automatizare-foi-de-parcurs",
    kind: "article",
    title:
      "Cum reduci timpul la foile de parcurs cu automatizare (fără Excel) | AIFoiParcurs",
    description:
      "Cum automatizezi foile de parcurs online: riscurile Excel, reutilizarea rutelor, calcul km și consum, generare documente și economii de timp pentru firme cu una sau mai multe mașini.",
    headline: "Cum reduci timpul la foile de parcurs cu automatizare (fără Excel)",
    ogImage: "/og/automatizare-foi-de-parcurs.png",
    ogImageAlt: "Foi de parcurs fără Excel, cu automatizare",
  },
  {
    path: "/foi-parcurs-comodat",
    kind: "article",
    title:
      "Foi de parcurs și comodat: mașina personală folosită de PFA sau SRL | AIFoiParcurs",
    description:
      "Dacă mașina e pe persoană fizică, primul document e comodatul, nu foaia. Contract, decont de km, 50% vs 100% și ce verifică ANAF la PFA și SRL.",
    headline: "Foi de parcurs și comodat: mașina personală folosită de PFA sau SRL",
    ogImage: "/og/foi-parcurs-comodat.png",
    ogImageAlt: "Comodat și foaie de parcurs — mașina personală în PFA sau SRL",
  },
  {
    path: "/foaie-parcurs-casa-serviciu",
    kind: "article",
    title:
      "Traseul casă–serviciu pe foaia de parcurs: personal sau de lucru | AIFoiParcurs",
    description:
      "Drumul acasă–sediu nu e automat deplasare de serviciu. Când ANAF îl citește ca navetă personală, ce pui pe foaie și când e mai sigur 50%.",
    headline:
      "Traseul casă–serviciu pe foaia de parcurs: când e personal și când poate fi de lucru",
    ogImage: "/og/foaie-parcurs-casa-serviciu.png",
    ogImageAlt: "Traseul casă–serviciu pe foaia de parcurs",
  },
  {
    path: "/norma-de-consum-foaie-parcurs",
    kind: "article",
    title: "Norma de consum pe foaia de parcurs: cum o stabilești | AIFoiParcurs",
    description:
      "Norma proprie de consum nu e cifra din cartea tehnică. Cum o calculezi, cum o documentezi și cum o leagă ANAF de alimentări și rezervor.",
    headline: "Norma de consum pe foaia de parcurs: cum o stabilești și ce verifică ANAF",
    ogImage: "/og/norma-de-consum-foaie-parcurs.png",
    ogImageAlt: "Norma de consum pe foaia de parcurs",
  },
  {
    path: "/masina-personala-srl-comodat",
    kind: "news",
    title:
      "Mașina personală folosită de SRL: comodat, decont km și foaie de parcurs | AIFoiParcurs",
    description:
      "Când autoturismul e pe numele administratorului, cheltuielile nu trec automat pe SRL. Comodat sau decont de km, foaie de parcurs și ce verifică ANAF.",
    headline: "Mașina personală folosită de SRL: comodat, decont km și foaie de parcurs",
    ogImage: "/og/masina-personala-srl-comodat.png",
    ogImageAlt: "Mașina personală folosită de SRL — comodat și decont de km",
  },
  {
    path: "/regulament-intern-autoturism",
    kind: "news",
    title: "Regulament intern auto de firmă: clauze pentru deducere 100% | AIFoiParcurs",
    description:
      "Regulamentul intern care susține deducerea 100% pe autoturismul de firmă: ce clauze conține, cine îl semnează, ce verifică ANAF și de ce nu ține loc de foaie.",
    headline: "Regulament intern pentru autoturismul de firmă",
  },
  {
    path: "/bon-fiscal-vs-factura-combustibil",
    kind: "news",
    title: "Bon fiscal vs factură la combustibil: ce acceptă ANAF | AIFoiParcurs",
    description:
      "Bonul fiscal și factura sunt ambele acceptate de ANAF. Diferența e în cât de clar demonstrează legătura dintre alimentare, vehicul și firmă — mai ales la 100%.",
    headline: "Bon fiscal vs factură la combustibil: ce acceptă ANAF",
    ogImage: "/og/bon-fiscal-vs-factura-combustibil.png",
    ogImageAlt: "Bon fiscal vs factură la combustibil — ce acceptă ANAF",
  },
  {
    path: "/autoutilitara-vs-autoturism-deductibilitate",
    kind: "news",
    title:
      "Autoutilitară vs autoturism: deductibilitate 100% și foaie de parcurs | AIFoiParcurs",
    description:
      "Înmatricularea N1 nu dă deducere integrală. Sub 3,5 tone și cu cel mult 9 locuri, dubița stă în aceeași regulă ca autoturismul: 50% dacă e și personală, 100% doar cu foaie.",
    headline:
      "Autoutilitară sau autoturism: când deduci 100% și când îți trebuie foaie de parcurs",
  },
  {
    path: "/decont-kilometri",
    kind: "news",
    title: "Decont de kilometri: când îl folosești în loc de combustibil pe firmă | AIFoiParcurs",
    description:
      "Pentru cursele ocazionale cu mașina personală, firma plătește lei/km. Combustibilul rămâne al persoanei. Traseu, regulă și dovada plății — și ce cade la control.",
    headline: "Decont de kilometri: când îl folosești în loc de combustibil pe firmă",
  },
  {
    path: "/trend-deductibilitate-100-procente",
    kind: "news",
    title:
      "De ce tot mai multe firme trec de la deducere 50% la 100% în 2026 | AIFoiParcurs",
    description:
      "Costurile auto au crescut, fiscalitatea s-a înăsprit, iar automatizarea a eliminat birocrația. Tot mai multe firme și PFA-uri aleg deducerea integrală. Iată de ce.",
    headline: "De ce tot mai multe firme trec de la deducere 50% la 100% în 2026",
  },
  {
    path: "/masina-firma-angajati",
    kind: "news",
    title:
      "Mașina de firmă folosită de angajați: cine completează foaia | AIFoiParcurs",
    description:
      "Când angajatul conduce mașina firmei, foaia de parcurs se schimbă: cine semnează, ce documente trebuie la predare, mai mulți șoferi pe același vehicul și ce verifică ANAF.",
    headline: "Mașina de firmă folosită de angajați: cine completează foaia și ce documente trebuie",
  },
  {
    path: "/vanzare-masina-foaie-parcurs",
    kind: "news",
    title:
      "Vânzarea mașinii: cum se închide foaia de parcurs | AIFoiParcurs",
    description:
      "Foaia de parcurs se oprește în ziua predării, nu pe 31. Indexul din contract trebuie să coincidă cu foaia, iar bonurile de după vânzare nu mai țin de mașina vândută.",
    headline: "Ce se întâmplă cu foaia de parcurs când vinzi mașina sau o scoți din uz",
  },
  {
    path: "/leasing-operational-foaie-parcurs",
    kind: "news",
    title:
      "Leasing operațional: cine ține foaia de parcurs și ce deduci | AIFoiParcurs",
    description:
      "Proprietarul e firma de leasing, utilizatorul ești tu. Rata și combustibilul urmează aceeași regulă, 50% sau 100%. Foaia începe la predare și se oprește la returnare.",
    headline:
      "Leasing operațional: cine ține foaia de parcurs și ce deduci tu, nu firma de leasing",
  },
  {
    path: "/ghiduri",
    kind: "hub",
    title: "Ghiduri foi de parcurs | AIFoiParcurs",
    description:
      "Ghiduri practice pentru foi de parcurs: cerințe ANAF, deducere 100%, PFA, model, control, automatizare, comodat, casă–serviciu și norma de consum.",
    headline: "Ghiduri pentru foi de parcurs",
  },
  {
    path: "/termeni-si-conditii",
    kind: "legal",
    title: "Termeni și condiții | AIFoiParcurs",
    description:
      "Termenii și condițiile de utilizare a aplicației AIFoiParcurs: rolul aplicației, responsabilitățile utilizatorului, plăți și livrare digitală.",
    headline: "Termeni și condiții",
  },
  {
    path: "/politica-de-confidentialitate",
    kind: "legal",
    title: "Politica de confidențialitate | AIFoiParcurs",
    description:
      "Politica de confidențialitate AIFoiParcurs: ce date colectăm, în ce scop, cum le folosim și drepturile utilizatorilor conform GDPR.",
    headline: "Politica de Confidențialitate - AiFoiParcurs",
  },
  {
    path: "/politica-gdpr",
    kind: "legal",
    title: "Politica GDPR | AIFoiParcurs",
    description:
      "Politica GDPR AIFoiParcurs: protecția datelor cu caracter personal, drepturile persoanelor vizate și măsurile de securitate aplicate.",
    headline: "Politica GDPR - Protecția datelor cu caracter personal",
  },
  {
    path: "/politica-de-livrare",
    kind: "legal",
    title: "Politica de livrare | AIFoiParcurs",
    description:
      "Modalitatea de livrare a serviciilor digitale AIFoiParcurs: activare imediată după plată, fără livrare fizică.",
    headline: "Politica de livrare",
  },
  {
    path: "/politica-de-rambursare",
    kind: "legal",
    title: "Politica de anulare și rambursare | AIFoiParcurs",
    description:
      "Politica de anulare și rambursare AIFoiParcurs: termene, condiții și procedura de cerere a rambursării pentru abonamente.",
    headline: "Politica de anulare și rambursare",
  },
];

const pagesByPath = new Map(publicPages.map((page) => [page.path, page]));

export function getPublicPage(path: string): PublicPage {
  const page = pagesByPath.get(path);
  if (!page) {
    throw new Error(`Unknown public page path: ${path}`);
  }
  return page;
}

export function pageUrl(path: string): string {
  return path === "/" ? `${SITE_URL}/` : `${SITE_URL}${path}`;
}

export type BreadcrumbCrumb = {
  name: string;
  path: string;
};

/** Short label for SERP and the visible trail (title before `:` / brand suffix). */
export function breadcrumbLabel(page: PublicPage): string {
  if (page.kind === "hub") {
    return "Ghiduri";
  }
  const withoutBrand = page.title.replace(/\s*\|\s*AIFoiParcurs\s*$/i, "").trim();
  const beforeColon = withoutBrand.split(":")[0]?.trim();
  return beforeColon || page.headline;
}

/**
 * Trail for JSON-LD and the visible nav.
 * Guides: Acasă › Ghiduri › articol. Hub: Acasă › Ghiduri. News/legal: Acasă › pagină.
 */
export function breadcrumbCrumbs(page: PublicPage): BreadcrumbCrumb[] {
  const home: BreadcrumbCrumb = { name: "Acasă", path: "/" };
  if (page.kind === "home") {
    return [home];
  }
  if (page.kind === "hub") {
    return [home, { name: "Ghiduri", path: page.path }];
  }
  if (page.kind === "article") {
    return [
      home,
      { name: "Ghiduri", path: "/ghiduri" },
      { name: breadcrumbLabel(page), path: page.path },
    ];
  }
  return [home, { name: breadcrumbLabel(page), path: page.path }];
}

export function ogImageUrl(page: PublicPage): string {
  return page.ogImage ? `${SITE_URL}${page.ogImage}` : DEFAULT_OG_IMAGE;
}

export function ogImageAltFor(page: PublicPage): string {
  return page.ogImageAlt ?? DEFAULT_OG_IMAGE_ALT;
}

/** Props for the client `<Seo>` component, kept in sync with prerender. */
export function seoFromPublicPage(path: string) {
  const page = getPublicPage(path);
  return {
    title: page.title,
    description: page.description,
    path: page.path,
    ogType: (page.kind === "article" || page.kind === "news" ? "article" : "website") as
      | "article"
      | "website",
    ogImage: ogImageUrl(page),
    ogImageAlt: ogImageAltFor(page),
  };
}
